/**
 * FIFO modal orchestrator.
 * One active Bootstrap/reactstrap modal at a time; others wait in arrival order.
 * Avoids body.modal-open races when multiple Modals fight over document.body.
 */

/** Gap before promoting the next queued modal after the active one closes. */
export const MODAL_QUEUE_PROMOTE_DELAY_MS = 500;

/** Delay after page init before any orchestrated modal may open. */
export const MODAL_INIT_DELAY_MS = 2000;

/** @typedef {keyof typeof ORCHESTRATED_MODALS} ModalId */

export const ORCHESTRATED_MODALS = {
  login: { key: "loginModal" },
  register: { key: "registerModal" },
  completeInfo: { key: "completeInfoModal" },
  registerSuccess: { key: "registerSuccessModal" },
  deposit: { key: "depositModal" },
  gameIframe: { key: "gameIframeModal" },
  gameLobby: { key: "gameLobbyModal" },
  profile: { key: "profileModal" },
  reportOverview: { key: "reportOverviewModal" },
  downline: { key: "downlineModal" },
  shareReferral: { key: "shareReferralModal" },
  spinWheelList: { key: "spinWheelListModal" },
  spinWheelReport: { key: "spinWheelReportModal" },
  dailyCheckin: { key: "dailyCheckinModal" },
  referralBonus: { key: "referralBonusModal" },
  announcement: { key: "showAnnouncementModal" },
  language: { key: "languageModal" },
  musicPlayer: { key: "musicPlayerModal" },
};

/** Opening one closes the other without re-queueing (auth swap). */
const REPLACE_GROUPS = [["login", "register"]];

export function getModalStateKey(modalId) {
  return ORCHESTRATED_MODALS[modalId]?.key;
}

function replaces(a, b) {
  if (!a || !b || a === b) return false;
  return REPLACE_GROUPS.some((group) => group.includes(a) && group.includes(b));
}

function buildFlags(active, queue) {
  const open = new Set(active ? [active] : []);
  const flags = {};
  for (const id of Object.keys(ORCHESTRATED_MODALS)) {
    flags[ORCHESTRATED_MODALS[id].key] = open.has(id);
  }
  // Queued modals stay closed in UI until promoted.
  void queue;
  return flags;
}

/** Promote next queued modal (FIFO front) when nothing is active. */
export function planPromote(orch) {
  const active = orch.modalActive || null;
  const queue = Array.isArray(orch.modalQueue) ? [...orch.modalQueue] : [];
  if (active) {
    return {
      modalActive: active,
      modalQueue: queue,
      flags: buildFlags(active, queue),
    };
  }
  const next = queue.length ? queue.shift() : null;
  return {
    modalActive: next,
    modalQueue: queue,
    flags: buildFlags(next, queue),
  };
}

/**
 * @param {{ modalActive: string|null, modalQueue: string[] }} orch
 * @param {string} modalId
 */
export function planOpen(orch, modalId) {
  if (!ORCHESTRATED_MODALS[modalId]) {
    return null;
  }

  const active = orch.modalActive || null;
  let queue = Array.isArray(orch.modalQueue) ? [...orch.modalQueue] : [];

  // Already active — no-op
  if (active === modalId) {
    return {
      modalActive: active,
      modalQueue: queue,
      flags: buildFlags(active, queue),
    };
  }

  // Already queued — keep FIFO position
  if (queue.includes(modalId)) {
    return {
      modalActive: active,
      modalQueue: queue,
      flags: buildFlags(active, queue),
    };
  }

  // Nothing open → activate
  if (!active) {
    return {
      modalActive: modalId,
      modalQueue: queue,
      flags: buildFlags(modalId, queue),
    };
  }

  // Auth swap: replace without queueing the displaced modal
  if (replaces(active, modalId)) {
    return {
      modalActive: modalId,
      modalQueue: queue,
      flags: buildFlags(modalId, queue),
    };
  }

  // Someone is active → append (FIFO)
  queue = [...queue, modalId];
  return {
    modalActive: active,
    modalQueue: queue,
    flags: buildFlags(active, queue),
  };
}

/**
 * @param {{ modalActive: string|null, modalQueue: string[] }} orch
 * @param {string} modalId
 * @param {{ deferPromote?: boolean }} [options]
 *   deferPromote: clear active but leave queue for a follow-up promote
 *   (avoids flicker when close+open auth swap in the same tick).
 */
export function planClose(orch, modalId, options = {}) {
  if (!ORCHESTRATED_MODALS[modalId]) {
    return null;
  }

  const active = orch.modalActive || null;
  let queue = Array.isArray(orch.modalQueue) ? [...orch.modalQueue] : [];

  // Dismiss from queue only
  if (active !== modalId) {
    queue = queue.filter((id) => id !== modalId);
    return {
      modalActive: active,
      modalQueue: queue,
      flags: buildFlags(active, queue),
    };
  }

  if (options.deferPromote) {
    return {
      modalActive: null,
      modalQueue: queue,
      flags: buildFlags(null, queue),
    };
  }

  // Promote next (FIFO front)
  const next = queue.length ? queue.shift() : null;
  return {
    modalActive: next,
    modalQueue: queue,
    flags: buildFlags(next, queue),
  };
}

/**
 * Snapshot active/queue from boolean flags (recovery / first sync).
 * Prefers explicit orch fields when present.
 */
export function readOrchestratorState(appState) {
  // Once initialized (modalQueue is always an array in app state), trust orch fields.
  if (Array.isArray(appState.modalQueue)) {
    return {
      modalActive: appState.modalActive || null,
      modalQueue: [...appState.modalQueue],
    };
  }

  // Legacy recovery: first open flag wins as active; rest keep object-key order in queue.
  let active = null;
  const queue = [];

  for (const id of Object.keys(ORCHESTRATED_MODALS)) {
    const key = ORCHESTRATED_MODALS[id].key;
    if (!appState[key]) continue;
    if (!active) {
      active = id;
    } else {
      queue.push(id);
    }
  }

  return { modalActive: active, modalQueue: queue };
}
